import { Component, h, Prop, Fragment, Method, Host, Element } from '@stencil/core';
import Swiper from 'swiper';
import { Navigation, Pagination, FreeMode, Thumbs, Keyboard, Controller, EffectFade, Autoplay } from 'swiper/modules';
import { SwiperOptions } from 'swiper/types/swiper-options';
import { ComponentDirection } from '../../interface';

/**
 * @status Stable
 *
 * @slot - Main content of the swiper.
 * @slot navigation - Custom navigation buttons.
 */
@Component({
  tag: 'o2-swiper',
  styleUrl: 'swiper.scss',
})
export class O2Swiper {
  private swiper: HTMLElement;
  private options: SwiperOptions;
  private swiperInstance: Swiper;

  @Element() private el: HTMLO2SwiperElement;

  /**
   * Turns navigation arrows on/off. Default false.
   */
  @Prop() navigation?: boolean = false;

  /**
   * Turns clickable pagination dots on/off. Default false.
   */
  @Prop() pagination?: boolean = false;

  /**
   * Displays active slide centered. Default false.
   */
  @Prop() centeredSlides?: boolean = false;

  /**
   * Enables free mode functionality. Default false.
   */
  @Prop() freeMode?: boolean = false;

  /**
   * Enables slide loop. Default false.
   */
  @Prop() loop?: boolean = false;

  /**
   * Defines number of slides per view. Default is 'auto'. Set only numbers.
   */
  @Prop() slidesPerView?: number;

  /**
   * If slidesPerView is set, this will set the gutter spacing between them. Set number in pixels.
   */
  @Prop() spaceBetween?: number = 32;

  /**
   * Set direction of slides. Default is horizontal.
   */
  @Prop() direction?: ComponentDirection = 'horizontal';

  /**
   * Custom options for swiper will be provided with setOptions() method.
   */
  @Prop() customOptions?: boolean = false;

  /**
   * Set custom Swiper options.
   */
  @Method()
  async setOptions(customOptions: SwiperOptions, mergeOptions: boolean = true) {
    if (mergeOptions) {
      this.options = { ...this.options, ...customOptions };
    } else {
      this.options = customOptions;
    }
  }

  /**
   * Initialize Swiper.
   *
   * @returns Swiper instance.
   */
  @Method()
  async init(): Promise<Swiper> {
    this.swiperInstance = new Swiper(this.swiper, this.options);
    return this.swiperInstance;
  }

  /**
   * Updates swiper instance after some render changes.
   */
  @Method()
  async update(): Promise<void> {
    return this.swiperInstance.update();
  }

  // private dynamicImportSwiperModules = async () => {
  //   let modules = [];

  //   let { Thumbs } = await import('swiper');
  //   modules.push(Thumbs);

  //   console.log('DI thumbs: ', Thumbs);
  //   console.log('DI modules: ', modules);
  //   return modules;
  // };

  /**
   * Intialize default swiper options.
   */
  private setDefaultOptions = () => {
    const navigationNextEl = (this.el.querySelector('.o2-swiper__button-next') as HTMLElement) || '.o2-swiper__button-next';
    const navigationPrevEl = (this.el.querySelector('.o2-swiper__button-prev') as HTMLElement) || '.o2-swiper__button-prev';

    const paginationEl = (this.el.querySelector('.o2-swiper__pagination') as HTMLElement) || '.o2-swiper__pagination';

    this.options = {
      // TODO dynamic modules
      modules: [Keyboard, Navigation, Pagination, FreeMode, Thumbs, Controller, EffectFade, Autoplay],
      wrapperClass: 'o2-swiper__wrapper',
      slideClass: 'o2-swiper__slide',
      speed: 500,
      direction: this.direction === 'vertical' ? 'vertical' : 'horizontal',
      // mobile slides
      slidesPerView: this.slidesPerView ? this.slidesPerView : 'auto',
      spaceBetween: this.spaceBetween,
      loop: this.loop,
      freeMode: this.freeMode,
      centeredSlides: this.centeredSlides,
      navigation: this.navigation
        ? {
            nextEl: navigationNextEl,
            prevEl: navigationPrevEl,
          }
        : false,
      pagination: this.pagination
        ? {
            el: paginationEl,
            clickable: true,
          }
        : false,
    };
  };

  connectedCallback() {
    this.setDefaultOptions();
  }

  componentDidRender() {
    if (!this.customOptions) {
      this.init();
    }
  }

  render() {
    return (
      <Host>
        <div class={`o2-swiper ${!this.slidesPerView ? 'slides-per-view-auto' : ''}`} ref={el => (this.swiper = el)}>
          <div class="o2-swiper__wrapper">
            <slot />
          </div>
          {this.navigation ? (
            <>
              <o2-icon color="blue-action" name="chevron-right" class="o2-swiper__button-next"></o2-icon>
              <o2-icon color="blue-action" name="chevron-left" class="o2-swiper__button-prev"></o2-icon>
            </>
          ) : (
            <slot name="navigation"></slot>
          )}
          {this.pagination ? <div class="o2-swiper__pagination"></div> : ''}
        </div>
      </Host>
    );
  }
}
